import React, { useState } from "react";
import * as FaIcons from "react-icons/fa";
import { EnumStatusConsultation, IHistory } from "../../interfaces/history";
import "./CompanyCard.css";
import { CustomPagination } from "../Table/custom-pagination";
import { formatCNPJ } from "../../Utils/Spinner/formatCNPJ";
import { FaSearchIcon, FaSearchIconA } from "../../pages/OCRPage";
import { useHistory } from "../../hooks/useHistory";
import { ConfirmConcludeModal } from "../ConfirmConcludeModal/ConfirmConcludeModal.tsx";
import DetailsModal from "../DetailsModal/DetailsModal";
import ChangeCnpjStatusModal from "../ChangeStatusModal/ChangeCnpjStatusModal";

interface CompanyCardsProps {
  companies: IHistory[];
  page: number;
  setPage: (page: number) => void;
  total: number;
}

const FaChevronDownIcon =
  FaIcons.FaChevronDown as unknown as React.FC<React.SVGProps<SVGSVGElement>>;
const FaChevronRightIcon =
  FaIcons.FaChevronRight as unknown as React.FC<React.SVGProps<SVGSVGElement>>;
const FaExchangeAltIcon =
  FaIcons.FaExchangeAlt as unknown as React.FC<React.SVGProps<SVGSVGElement>>;

const norm = (s?: string) =>
  (s || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toUpperCase();

const safeParse = (s?: string | null) => {
  if (!s) return null;
  try { return JSON.parse(s); } catch { return null; }
};

const classFrom4AxonTag = (tag?: string) => {
  const v = norm(tag);
  if (v.includes("NEG")) return "bg-danger";
  if (v.includes("APROV")) return "bg-success";
  if (v.includes("ANAL")) return "bg-info";
  if (v.includes("PROCESS")) return "bg-warning";
  return "bg-secondary";
};

function computeCompanyTag(partnersCount: number, tagPorCpf: string): string {
  const v = norm(tagPorCpf);
  if (!partnersCount) return tagPorCpf || "—";

  if (v.startsWith("NEG")) return "Negar";
  if (v.startsWith("APROV")) return "Aprovar";
  return "Em análise";
}

export const CompanyCards = ({
  companies,
  page,
  setPage,
  total,
}: CompanyCardsProps) => {
  const {
    mutationConcluir,
    openConcludeModal,
    showConcludeModal,
    confirmConclude,
    setShowConcludeModal,
    pendingCompany,
    openChangeStatusModal,
    showChangeStatusModal,
    setShowChangeStatusModal,
    companyToChange,
    confirmAlterarStatus,
    isChangingStatus,
  } = useHistory();

  const [showDetails, setShowDetails] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<IHistory | null>(null);
  const isConcluding = mutationConcluir.status === "pending";

  function getStatusBadge(status: number) {
    switch (status) {
      case EnumStatusConsultation.approved:
        return { label: "Aprovado", className: "bg-success" };
      case EnumStatusConsultation.completed:
        return { label: "Finalizado", className: "bg-success" };
      case EnumStatusConsultation.disapproved:
        return { label: "Reprovado", className: "bg-danger" };
      case EnumStatusConsultation.inProcess:
        return { label: "Em processo", className: "bg-warning" };
      case EnumStatusConsultation.inAnalyzing:
        return { label: "Em análise", className: "bg-info" };
      default:
        return { label: "Desconhecido", className: "bg-secondary" };
    }
  }

  const FaChevronDownIcon = FaIcons.FaChevronDown as unknown as React.FC<
    React.SVGProps<SVGSVGElement>
  >;

  const FaChevronRightIcon = FaIcons.FaChevronRight as unknown as React.FC<
    React.SVGProps<SVGSVGElement>
  >;

  const FaCheckCircleIcon = FaIcons.FaCheckCircle as unknown as React.FC<
    React.SVGProps<SVGSVGElement>
  >;

  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  const toggleRow = (index: number) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  const handleOpenDetails = (company: IHistory) => {
    setSelectedCompany(company);
    setShowDetails(true);
  };

  return (
    <div className="table-responsive mt-4">
      <table className="table table-bordered">
        <thead style={{ backgroundColor: "#3f3f3f", color: "#fff" }}>
          <tr>
            <th>Protocolo</th>
            <th>CNPJ</th>
            <th>Nome Fantasia</th>
            <th>Data Início</th>
            <th>Data Fim</th>
            <th>Nível de Risco</th>
            <th>Qtd Sócios</th>
            <th>Status</th>
            <th>Detalhes</th>
            {/* <th>Revisão</th> */}
          </tr>
        </thead>
        <tbody>
          {companies.map((company, index) => {
            const tagPorCpf =
              (company as any)?.valueTagRecomendacao ??
              (company as any)?.tagRecomendacao ??
              "—";

            // badge da empresa baseada na regra descrita
            const companyTag = computeCompanyTag(
              company.partners?.length ?? 0,
              tagPorCpf
            );
            const companyStatusClass = classFrom4AxonTag(companyTag);

            // Device info (para exibir na linha expandida)
            const deviceRes = (company as any)?.allowMeDeviceValidationResult as
              | { responseJson?: string | null; deviceIp?: string | null }
              | undefined;
            const deviceParsed = safeParse(deviceRes?.responseJson) as
              | {
                DeviceId?: string;
                DeviceInfo?: {
                  Device_Location_Latitude?: number;
                  Device_Location_Longitude?: number;
                };
              }
              | null;
            const deviceIp = deviceRes?.deviceIp || "—";
            const deviceId = deviceParsed?.DeviceId ?? "—";
            const geoLat =
              deviceParsed?.DeviceInfo?.Device_Location_Latitude ?? "—";
            const geoLong =
              deviceParsed?.DeviceInfo?.Device_Location_Longitude ?? "—";

            return (
              <React.Fragment key={index}>
                <tr style={{ cursor: "pointer" }}>
                  <td>{company.protocol}</td>
                  <td>
                    <strong>{formatCNPJ(company?.document)}</strong>
                  </td>
                  <td>
                    {company?.consultationDataPH3A?.nameFantasia ??
                      company?.companyName ??
                      "—"}
                  </td>
                  <td>{new Date(company?.data).toLocaleString()}</td>
                  <td>
                    {company.endDate
                      ? new Date(company?.endDate).toLocaleString()
                      : "—"}
                  </td>
                  <td>Baixo Risco</td>
                  <td>{company.partners.length}</td>
                  <td>
                    <span className={`badge ${companyStatusClass}`}>
                      {companyTag}
                    </span>
                  </td>
                  <td className="text-center">
                    <div className="d-flex justify-content-center align-items-center gap-1">
                      <button
                        onClick={() => toggleRow(index)}
                        className="btn btn-sm btn-light"
                        title="Ver mais"
                      >
                        {expandedIndex === index ? (
                          <FaChevronDownIcon />
                        ) : (
                          <FaChevronRightIcon />
                        )}
                      </button>

                      {/* {!norm(companyTag).startsWith("NEG") && (
                        <button
                          onClick={() => openConcludeModal(company)}
                          className="btn btn-sm btn-success"
                          title="Concluir consulta"
                          disabled={isConcluding}
                        >
                          <FaCheckCircleIcon className="mb-1" />
                        </button> */}
                    </div>
                  </td>
                  {/* <td>
                    <button
                      onClick={() => openChangeStatusModal(company)}
                      className="btn btn-sm btn-outline-primary"
                      title="Alterar status"
                    >
                      <FaExchangeAltIcon />
                    </button>
                  </td> */}
                </tr>

                {expandedIndex === index && (
                  <tr>
                    <td colSpan={13} className="bg-light">
                      <table className="table table-sm table-borderless mb-0">
                        <thead>
                          <tr className="text-muted small">
                            <th>Nome</th>
                            <th>CPF</th>
                            <th>Tel</th>
                            <th>Email</th>
                            <th>Representante</th>
                            <th>Status (4Axon)</th>
                            <th>Hotline Bureau</th>
                            <th>IP</th>
                            <th>DeviceId</th>
                            <th>Geo (lat/long)</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {company.partners.map((p, i) => {
                            // Status do Partner = valueTagRecomendacao (4Axon)
                            const partnerStatus = tagPorCpf || "—";
                            const partnerStatusCls =
                              classFrom4AxonTag(partnerStatus);

                            return (
                              <tr key={i} className="small">
                                <td>{p.name}</td>
                                <td>{p.cpf}</td>
                                <td>{p.phone}</td>
                                <td>{p.email}</td>
                                <td>{p.representanteLegal ? "Sim" : "Não"}</td>
                                <td>
                                  {partnerStatus}
                                </td>
                                <td>
                                  {company.phoneDistinct ?? "Não encontrado"}
                                </td>
                                <td>{deviceIp}</td>
                                <td>{deviceId}</td>
                                <td>{`${geoLat}, ${geoLong}`}</td>
                                <td className="text-center">
                                  {i === 0 && (
                                    <button
                                      onClick={() => handleOpenDetails(company)}
                                      className="btn btn-sm btn-outline-secondary"
                                      title="Ver detalhes"
                                    >
                                      <FaSearchIconA />
                                    </button>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>

      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <CustomPagination
          currentPage={page}
          totalPages={total}
          onPageChange={setPage}
        />
      </div>
      <DetailsModal
        show={showDetails}
        onHide={() => setShowDetails(false)}
        data={selectedCompany}
      />
      <ConfirmConcludeModal
        show={showConcludeModal}
        onHide={() => setShowConcludeModal(false)}
        onConfirm={confirmConclude}
        data={pendingCompany}
        loading={isConcluding}
      />

      <ChangeCnpjStatusModal
        show={showChangeStatusModal}
        onHide={() => setShowChangeStatusModal(false)}
        protocol={companyToChange?.protocol}
        currentStatus={companyToChange?.status}
        onConfirm={confirmAlterarStatus}
        loading={isChangingStatus}
      />
    </div>
  );
};
